// Customizable Area Start
import React, { CSSProperties } from "react";
import { createStyles, Theme } from "@material-ui/core/styles";
import {
  Container,
  Box,
  Typography,
  Button,
  IconButton,
  withStyles,
  TextField,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import clsx from "clsx";
import NewPostAddCaptionController, {
  Props,
} from "./NewPostAddCaptionController.web";
import { CaptionViews, NewPostCreationSteps } from "./enums";
import CustomButton from "../../../components/src/CustomButton.web";
import MediaComponent from "../../../components/src/MediaComponent.web";
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Pagination } from 'swiper';
import 'swiper/swiper-bundle.min.css';
import 'swiper/swiper.min.css';

// Register Swiper modules
SwiperCore.use([Pagination]);

const styles = (theme: Theme) =>
  createStyles({
    modal: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      padding: "30px 44px 40px",
      gap: "22px",
      background: "#FFF",
      [theme.breakpoints.down("sm")]: {
        width: "100%",
        minHeight: "100%",
      },
      [theme.breakpoints.down(450)]: {
        padding: "20px 0px"
      },
    },
    closeButton: {
      position: "absolute",
      top: "15px",
      right: "10px",
    },

    modalTitle: {
      alignSelf: "flex-start",
      fontSize: "32px",
      fontWeight: 700,
      lineHeight: "32px",
      letterSpacing: "-0.13px",
      [theme.breakpoints.down(650)]: {
        fontSize: "24px"
      },
    },
    modalHeaderView:{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      position: 'absolute',
      top: 16,
      left: 16,
    },

    previewContainer: {
      position: "relative",
    },

    imagePreviewContainer: {
      display: "flex",
      width: "320px",
      height: "506px",
      overflow: "hidden",
      borderRadius: "20px",
      backgroundColor: "#F2F2F2",

      "& > div": {
        width: "100%",
        height: "100%",
      },

      "& > video": {
        width: "100%",
        objectFit: "cover",
      },
    },

    captionButtonsContainer: {
      position: "absolute",
      top: "40px",
      right: "21px",
      display: "flex",
      flexDirection: "column",
      gap: "14px",
      zIndex: 20,
    },

    captionButton: {
      width: "32px",
      minWidth: "32px",
      height: "32px",
      borderRadius: "50%",
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      border: "1px solid #FFF",
      color: "#FFF",
      fontSize: "10px",
      fontWeight: 700,
      textTransform: "none",
      fontFamily: "Inter, 'sans-serif'",
    },

    pollContainer: {
      position: "absolute",
      bottom: "121px",
      left: 0,
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "12px",
      zIndex: 15,
    },

    pollQuestion: {
      width: "90%",

      "& > div": {
        height: "100%",
      },

      "& > div.MuiInput-underline:hover:not(.Mui-disabled):before": {
        borderBottom: "none",
      },

      "& > div.MuiInput-underline:after": {
        borderBottom: "none",
      },

      "& input": {
        textAlign: "left",
        fontSize: "14px",
        fontWeight: 700,
        color: "#000",
        fontFamily: "Inter, 'sans-serif'",
        [theme.breakpoints.down('sm')]: {
          fontSize: "16px", // Prevent mobile zoom
        },

        "&::placeholder": {
          fontSize: "14px",
          fontWeight: 500,
          textAlign: "center",
          color: "#000",
          opacity: 1,
          [theme.breakpoints.down('sm')]: {
            fontSize: "16px", // Prevent mobile zoom
          },
        },
        "&:focus::placeholder": {
          opacity: 0
        }
      },

      "& textarea": {
        textAlign: "left",
        fontSize: "14px",
        fontWeight: 700,
        color: "#000",
        fontFamily: "Inter, 'sans-serif'",
        [theme.breakpoints.down('sm')]: {
          fontSize: "16px", // Prevent mobile zoom
        },

        "&::placeholder": {
          fontSize: "14px",
          fontWeight: 500,
          textAlign: "center",
          color: "#000",
          opacity: 1,
          [theme.breakpoints.down('sm')]: {
            fontSize: "16px", // Prevent mobile zoom
          },
        },
        "&:focus::placeholder": {
          opacity: 0
        }
      },

      "& .MuiInput-underline:before": {
        borderBottom: "none",
      },
    },

    pollAnswer: {
      minWidth: "160px",
      minHeight: "31px",
      borderRadius: "10px",
      backgroundColor: "#FFF",
      paddingLeft:10,

      "& > div": {
        height: "100%",
      },

      "& > div.MuiInput-underline:hover:not(.Mui-disabled):before": {
        borderBottom: "none",
      },

      "& > div.MuiInput-underline:after": {
        borderBottom: "none",
      },

      "& input": {
        textAlign: "left",
        fontSize: "12px",
        fontWeight: 700,
        fontFamily: "Inter, 'sans-serif'",
        [theme.breakpoints.down('sm')]: {
          fontSize: "16px", // Prevent mobile zoom
        },

        "&::placeholder": {
          fontSize: "12px",
          fontWeight: 700,
          textAlign: "center",
          color: "#000",
          opacity: 1,
          [theme.breakpoints.down('sm')]: {
            fontSize: "16px", // Prevent mobile zoom
          },
        },
        "&:focus::placeholder": {
          opacity: 0
        }
      },

      "& textarea": {
        textAlign: "left",
        fontSize: "12px",
        fontWeight: 700,
        fontFamily: "Inter, 'sans-serif'",
        [theme.breakpoints.down('sm')]: {
          fontSize: "16px", // Prevent mobile zoom
        },

        "&::placeholder": {
          fontSize: "12px",
          fontWeight: 700,
          textAlign: "center",
          color: "#000",
          opacity: 1,
          [theme.breakpoints.down('sm')]: {
            fontSize: "16px", // Prevent mobile zoom
          },
        },
        "&:focus::placeholder": {
          opacity: 0
        }
      },

      "& .MuiInput-underline:before": {
        borderBottom: "none",
      },
    },

    caption: {
      position: "absolute",
      top: "60%",
      left: "50%",
      transform: "translate(-50%, 0)",
      width: "90%",
      height: "96px",
      padding: "6px 10px",
      overflow: "hidden",
      resize: "none",
      borderRadius: "5px 5px 20px 5px",
      boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
      textAlign: "center",
      fontSize: "16px",
      lineHeight: "28px",
      fontWeight: 500,
      fontFamily: "Inter, 'sans-serif'",
      border: "none",
      zIndex: 15,
      "&:focus-visible": {
        outline: "none",
      },
    },

    dotsContainer: {
      position: "absolute",
      bottom: "108px",
      left: "50%",
      transform: "translate(-50%, 0)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "7px",
      zIndex: 10,
    },

    filesChoiceDots: {
      width: "7px",
      height: "7px",
      borderRadius: "50%",
      cursor: "pointer",
      boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    },

    swiperContainer: {
      width: "320px",
      height: "506px",
      borderRadius: "20px",
      overflow: "hidden",
      backgroundColor: "#F2F2F2",
      position: "relative",
      zIndex: 1,
      "& .swiper-slide": {
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      "& .swiper-pagination": {
        bottom: "108px !important",
        zIndex: 10,
        "& .swiper-pagination-bullet": {
          width: "7px",
          height: "7px",
          backgroundColor: "rgba(255, 255, 255, 0.50)",
          opacity: 1,
          boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
          "&.swiper-pagination-bullet-active": {
            backgroundColor: "#FFC629",
          },
        },
      },
    },

    stepButtonsContainer: {
      position: "absolute",
      bottom: "29px",
      left: "50%",
      transform: "translate(-50%, 0)",
      display: "flex",
      gap: "10px",
      zIndex: 20,
    },

    stepButton: {
      height: "44px",
      "&:hover" : {
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        border: "1px solid #fff !important",
      }
    },
  });

const webStyles: Record<string, CSSProperties> = {};

class NewPostAddCaption extends NewPostAddCaptionController {
  swiperInstance: any = null;

  constructor(props: Props) {
    super(props);
  }

  componentDidUpdate(prevProps: Props, prevState: any) {
    // Sync Swiper when currentFile changes externally (e.g., from dots)
    if (
      prevState.currentFile !== this.state.currentFile &&
      this.swiperInstance
    ) {
      this.swiperInstance.slideTo(this.state.currentFile);
    }
  }

  handleSwiperInit = (swiper: any) => {
    this.swiperInstance = swiper;
  };

  render() {
    const {
      files,
      classes,
      poll,
      view,
      caption,
      setStep,
      closeModal,
      setPollQuestion,
      setPollAnswer1,
      setPollAnswer2,
      setCaptionView,
      setCaption,
    } = this.props;

    const { currentFile } = this.state;

    return (
      <Container
        id="confirmFileContainer"
        maxWidth={false}
        className={classes.modal}
        onClick={(e) => e.stopPropagation()}
      >
        <IconButton
          id="closeModalButton"
          className={classes.closeButton}
          onClick={closeModal}
        >
          <CloseIcon />
        </IconButton>
        <Typography className={classes.modalTitle}>New post</Typography>
        <Box className={classes.previewContainer}>
          {files.length > 1 ? (
            <Swiper
              onSwiper={this.handleSwiperInit}
              className={classes.swiperContainer}
              spaceBetween={0}
              slidesPerView={1}
              pagination={{ clickable: true }}
              onSlideChange={(swiper) => {
                this.setCurrentFile(swiper.activeIndex);
              }}
              initialSlide={currentFile}
              style={{
                width: "320px",
                height: "506px",
              }}
            >
              {files.map((file, index) => (
                <SwiperSlide key={index}>
                  <Box className={classes.imagePreviewContainer}>
                    <MediaComponent url={file.preview} type={file?.type} />
                  </Box>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <Box className={classes.imagePreviewContainer}>
              <MediaComponent url={files[currentFile].preview} type={files[currentFile]?.type} />
            </Box>
          )}

          <Box className={classes.captionButtonsContainer}>
            <Button
              id="setPollButton"
              data-test-id="pollButton"
              className={classes.captionButton}
              onClick={() => setCaptionView(CaptionViews.POLL)}
            >
              Poll
            </Button>
            {/* <Button
              id="setCaptionButton"
              data-test-id="captionAddBtn"
              className={classes.captionButton}
              onClick={() => setCaptionView(CaptionViews.CAPTION)}
            >
              Aa
            </Button> */}
          </Box>

          {view === CaptionViews.POLL && (
            <Box className={classes.pollContainer}>
<TextField
  id="pollQuestionInput"
  value={poll.question}
  placeholder="Ask A Question"
  onChange={(e) => {
    const text = e.target.value;
    
    // Split into lines
    const lines = text.split("\n");
    
    // Allow only first 3 lines
    if (lines.length <= 3) {
      setPollQuestion(text);
    } else {
      // Keep only the first 3 lines
      setPollQuestion(lines.slice(0, 3).join("\n"));
    }
  }}
  onKeyDown={(e) => {
    const lines = poll.question.split("\n");
    if (e.key === "Enter" && lines.length >= 3) {
      e.preventDefault(); // Stop adding new line
    }
  }}
  className={classes.pollQuestion}
  multiline
  maxRows={3}
  InputProps={{
    style: {
      backgroundColor: "#FFF",  // white background
      color: "black",           // text color for readability
      whiteSpace: "pre-wrap",   // ensures wrapping
      wordWrap: "break-word",   // breaks long words
      padding:10,
      borderRadius:10,
      textAlign: "center"       // center the text
    },
    inputProps: {
      style: {
        textAlign: "center"     // center the placeholder text
      }
    }
  }}
/>


              <TextField
                id="answer1Input"
                value={poll.answer1}
                placeholder="Answer 1"
                onChange={(e) => {
                  const text = e.target.value;
                  
                  // Split into lines
                  const lines = text.split("\n");
                  
                  // Allow only first 2 lines
                  if (lines.length <= 2) {
                    setPollAnswer1(text);
                  } else {
                    // Keep only the first 2 lines
                    setPollAnswer1(lines.slice(0, 2).join("\n"));
                  }
                }}
                onKeyDown={(e) => {
                  const lines = poll.answer1.split("\n");
                  if (e.key === "Enter" && lines.length >= 2) {
                    e.preventDefault(); // Stop adding new line
                  }
                }}
                className={classes.pollAnswer}
                multiline
                maxRows={2}
                InputProps={{
                  style: {
                    textAlign: "center"
                  },
                  inputProps: {
                    style: {
                      textAlign: "center",
                      wordWrap: 'break-word'
                    }
                  }
                }}
              />
              <TextField
                id="answer2Input"
                placeholder="Answer 2"
                value={poll.answer2}
                onChange={(e) => {
                  const text = e.target.value;
                  
                  // Split into lines
                  const lines = text.split("\n");
                  
                  // Allow only first 2 lines
                  if (lines.length <= 2) {
                    setPollAnswer2(text);
                  } else {
                    // Keep only the first 2 lines
                    setPollAnswer2(lines.slice(0, 2).join("\n"));
                  }
                }}
                onKeyDown={(e) => {
                  const lines = poll.answer2.split("\n");
                  if (e.key === "Enter" && lines.length >= 2) {
                    e.preventDefault(); // Stop adding new line
                  }
                }}
                className={classes.pollAnswer}
                multiline
                maxRows={2}
                InputProps={{
                  style: {
                    textAlign: "center"
                  },
                  inputProps: {
                    style: {
                      textAlign: "center",
                      wordWrap: 'break-word'
                    }
                  }
                }}
              />
            </Box>
          )}

          {view === CaptionViews.CAPTION && (
            <textarea
              placeholder="write a comment"
              id="captionInput"
              value={caption}
              maxLength={100}
              onChange={(e) => {
                const text = e.target.value;
                
                // Split into lines
                const lines = text.split("\n");
                
                // Allow only first 3 lines
                if (lines.length <= 3) {
                  setCaption(text);
                } else {
                  // Keep only the first 3 lines
                  setCaption(lines.slice(0, 3).join("\n"));
                }
              }}
              onKeyDown={(e) => {
                const lines = caption.split("\n");
                if (e.key === "Enter" && lines.length >= 3) {
                  e.preventDefault(); // Stop adding new line
                }
              }}
              className={classes.caption}
            />
          )}


          <Box className={classes.stepButtonsContainer}>
            <CustomButton
              id="rejectFilesButton"
              data-test-id="discard-button"
              className={classes.stepButton}
              btnType="secondary"
              onClick={() => {
                setCaption("")
                setPollQuestion("Ask a question")
                setPollAnswer1("Answer 1")
                setPollAnswer2("Answer 2")
                setCaptionView(CaptionViews.NONE)
                setStep(NewPostCreationSteps.SELECT_FILE);
              }}
            >
              Discard
            </CustomButton>
            <CustomButton
              id="confirmFilesButton"
              className={classes.stepButton}
              onClick={() => setStep(NewPostCreationSteps.COVER_SELECTION)}
            >
              Next
            </CustomButton>
          </Box>
        </Box>
      </Container>
    );
  }
}

export { NewPostAddCaption };
export default withStyles(styles)(NewPostAddCaption);
// Customizable Area End
